import { ReactElement, useEffect, useRef } from "react";
import { Box } from "@chakra-ui/react";

type CircularGaugeProps = {
  score: number;
  maxScore?: number;
  size?: number;
};

export function CircularGauge({
  score,
  maxScore = 100,
  size = 250,
}: Readonly<CircularGaugeProps>): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    const outerRadius = size / 2 - 10;
    const innerRadius = outerRadius - 15;
    const backgroundRadius = innerRadius - 5;

    // Start angle: 225 degrees (bottom-left), End angle: 495 degrees (bottom-right)
    const START_DEGREE = 150;
    const END_DEGREE = 390;
    const startAngle = (Math.PI * START_DEGREE) / 180;
    const endAngle = (Math.PI * END_DEGREE) / 180;

    // Fill inner circle with white background and shadow
    ctx.shadowColor = "rgba(0, 0, 0, 0.15)";
    ctx.shadowBlur = 12;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.beginPath();
    ctx.arc(centerX, centerY, backgroundRadius, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();

    // Reset shadow to avoid affecting other elements
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Draw gray background circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, backgroundRadius, 0, Math.PI * 2);
    ctx.strokeStyle = "#E8E8E8";
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw gradient arc
    const gradient = ctx.createConicGradient(startAngle, centerX, centerY);
    const gradientRatio = (END_DEGREE - START_DEGREE) / 360;
    gradient.addColorStop(0, "#DC143C"); // Red
    gradient.addColorStop(0.25 * gradientRatio, "#FF8C00"); // Orange
    gradient.addColorStop(0.5 * gradientRatio, "#FFD700"); // Yellow
    gradient.addColorStop(0.75 * gradientRatio, "#9ACD32"); // Green
    gradient.addColorStop(1 * gradientRatio, "#9ACD32"); // Green

    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRadius, startAngle, endAngle);
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.stroke();

    // Calculate indicator position based on score
    const percentage = Math.min(Math.max(score / maxScore, 0), 1);
    const angleRange = endAngle - startAngle;
    const indicatorAngle = startAngle + angleRange * percentage;

    // Draw indicator (small circle)
    const indicatorX = centerX + outerRadius * Math.cos(indicatorAngle);
    const indicatorY = centerY + outerRadius * Math.sin(indicatorAngle);

    // Add shadow to indicator (proportional to inner circle shadow)
    const indicatorRadius = 8;
    ctx.shadowColor = "rgba(0, 0, 0, 0.15)";
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    ctx.beginPath();
    ctx.arc(indicatorX, indicatorY, indicatorRadius, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    ctx.strokeStyle = "#CCCCCC";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Reset shadow
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }, [score, maxScore, size]);

  return (
    <Box position="relative" width={`${size}px`} height={`${size}px`}>
      <canvas ref={canvasRef} width={size} height={size} />
    </Box>
  );
}
